import React, { FunctionComponent, useContext, useEffect, useMemo, useState, useCallback } from 'react';
import { useKeycloak } from '@react-keycloak/web';
import { useHistory } from 'react-router-dom';
import BarraPesquisa from 'pages/pesquisa/barraPesquisa/BarraPesquisa';
import Loading from 'components/Loading';
import CursoLivreCard from './CursoLivreCard';
import IntermediatePage from '../components/IntermediatePage';
import { FiltrarCurso } from './components/FiltrarCurso';
import CursoLivreCarousel from './components/CursoLivreCarousel';
import { CursoLivreService } from 'core/http/service/CursoLivre.service';
import AdminRecursosCostumizados from 'core/http/service/admin/AdminRecursosCostumizados.service';
import { FadCoursesService } from 'core/http/service/ferramentaAutoria/FadCourses.service';
import { PaginaIntermediariaService } from 'core/http/service/PaginaIntermediaria.service';
import AdminCursoLivre from 'models/admin/AdminCursoLivre';
import AdminPaginaIntermediaria from 'models/admin/AdminPaginaIntermediaria';
import { FadCoursesExclusiveModel } from 'models/ferramentaAutoria/FadCoursesExclusiveModel';
import { GTM_GA4_Ecommerce_View_Item_List, GTM_Page_Intermediaria } from 'components/GoogleTagManager';
import cursosLivresSorter from 'pages/admin/components/cursoLivre/cursoLivreSorter';
import { getLoginRedirectUri, limparRedirecionamentosPosLogin } from 'core/util/StorageRedirectRulesHelper';
import { PesquisaContext } from 'pages/home/context/PesquisaContext';
import ElipseWhite from '../assets/ElipseWhite.svg';
import { CategoriaSolucoesPEService } from 'core/http/service/acessosExclusivos/CategoriaSolucoesPE.service';
import ResgatedToken from 'pages/solucoes/components/solucoes/ResgatedToken';
import {
    CardsContainerInner,
    CloseModalButton,
    CreateAccountLink,
    CreateAccountText,
    CursoLivreDetailContainer,
    ExclusiveSection,
    ExclusiveSectionContent,
    ExclusiveSectionEllipse,
    ExclusiveSectionTitle,
    LoginModal,
    LoginModalButton,
    LoginModalDescription,
    LoginModalOverlay,
    LoginModalTitle,
    SectionContainer,
    SectionTitle,
    TokenActivationBar,
    TokenActivationBarWrapper,
    TokenActivationButton,
    TokenActivationText,
    TokenActivationTextContainer,
} from './CursoLivreDetail.styled';

type CursoParaCard = AdminCursoLivre & {
    isMatriculado?: boolean;
    isConcluido?: boolean;
    progresso?: number;
    isExclusivo?: boolean;
    isNovo?: boolean;
};

const isFadCurso = (curso: CursoParaCard): boolean => {
    return curso.isExclusivo === true;
};

const isCursoNovo = (curso: AdminCursoLivre): boolean => {
    if (!curso.dataCriacao) {
        return false;
    }

    const dataCriacaoDate = new Date(curso.dataCriacao);

    const doisMesesAtras = new Date();
    doisMesesAtras.setMonth(doisMesesAtras.getMonth() - 2);

    return dataCriacaoDate >= doisMesesAtras;
};

/**
 * Adapta o retorno do endpoint de Cursos Exclusivos
 * para o formato que o CursoLivreCard já utiliza.
 */
const mapExclusiveCourseToCard = (curso: FadCoursesExclusiveModel): CursoParaCard => {
    const cardCurso = new AdminCursoLivre() as CursoParaCard;

    cardCurso.codigo = curso.codigoCurso;
    cardCurso.titulo = curso.nomeCurso;
    cardCurso.descricao = '';
    cardCurso.url = curso.urlCurso;
    cardCurso.urlAcessarCurso = curso.urlCurso;
    cardCurso.filePath = curso.filePath ?? '';
    cardCurso.dataCriacao = curso.dataCriacao ? new Date(curso.dataCriacao) : null;

    cardCurso.isExclusivo = true;
    cardCurso.isMatriculado = curso.progresso > 0;
    cardCurso.isConcluido = curso.progresso >= 100;
    cardCurso.progresso = curso.progresso;
    cardCurso.isNovo = curso.isNovo;

    /*
     * Esses campos são utilizados pelo CursoLivreCard,
     * mas não vêm do endpoint FAD.
     */
    cardCurso.prazoInscricao = null;
    cardCurso.dataExpiracao = null;
    cardCurso.emProducao = false;
    cardCurso.naoAplicavel = false;

    return cardCurso;
};

const CursoLivreDetail: FunctionComponent = () => {
    const [cursos, setCursos] = useState<CursoParaCard[]>([]);
    const [exclusiveCursos, setExclusiveCursos] = useState<CursoParaCard[]>([]);
    const [loading, setLoading] = useState<boolean>(false);
    const [statusFilter, setStatusFilter] = useState<StatusFilter>('TODOS');
    const [sortOption, setSortOption] = useState<SortOption | null>(null);
    const [dadosPagina, setDadosPagina] = useState<AdminPaginaIntermediaria>(new AdminPaginaIntermediaria());

    const history = useHistory();
    const { isSearchView } = useContext(PesquisaContext.Context);
    const { keycloak } = useKeycloak();

    type StatusFilter = 'TODOS' | 'NAO_INICIADOS' | 'EM_ANDAMENTO' | 'CONCLUIDOS';

    type SortOption = 'MAIS_NOVO' | 'MAIS_ANTIGO' | 'A_Z' | 'Z_A';

    const isLogged = !!keycloak.authenticated;
    const [showLoginModal, setShowLoginModal] = useState<boolean>(!isLogged);
    const [hasValidToken, setHasValidToken] = useState<boolean | null>(null);
    const [openTokenModal, setOpenTokenModal] = useState<boolean>(false);

    const paginaService = new PaginaIntermediariaService();
    const cursoLivreService = new CursoLivreService();
    const recursosCostumizadosService = new AdminRecursosCostumizados();
    const fadCoursesService = new FadCoursesService();

    const PAGINA_KEY: string = 'CURSO_LIVRE';

    const register = () => {
        const url = keycloak.createRegisterUrl({
            redirectUri: getLoginRedirectUri(),
        });
        location.assign(url);
    };

    const login = () => {
        history.push({
            pathname: '/login',
            state: { pathToRedirect: `${window.location.pathname}${window.location.search}${window.location.hash}` },
        });
    };

    useEffect(() => {
        window.scrollTo(0, 0);
    }, []);

    useEffect(() => {
        setShowLoginModal(!isLogged);
    }, [isLogged]);

    useEffect(() => {
        if (!isLogged && showLoginModal) {
            document.body.style.overflow = 'hidden';
        } else {
            document.body.style.overflow = '';
        }

        return () => {
            document.body.style.overflow = '';
        };
    }, [isLogged, showLoginModal]);

    const checkTokenStatus = useCallback(async () => {
        if (!isLogged) {
            setHasValidToken(false);
            return;
        }

        const email = keycloak.tokenParsed?.email ?? '';

        if (email.includes('@ias')) {
            setHasValidToken(true);
            return;
        }

        try {
            const serviceCategoriaSolucoes = new CategoriaSolucoesPEService();
            const response = await serviceCategoriaSolucoes.getSolucoesByUser();
            const tokenValidado = response.status === 200 && Array.isArray(response.data) && response.data.length > 0;
            setHasValidToken(tokenValidado);
        } catch (error) {
            console.error('Erro ao verificar status do token:', error);
            setHasValidToken(false);
        }
    }, [isLogged, keycloak.tokenParsed?.email]);

    useEffect(() => {
        checkTokenStatus();
    }, [checkTokenStatus]);

    const loadData = async () => {
        try {
            setLoading(true);

            /*
             * Cursos livres continuam sendo carregados
             * exatamente como antes.
             */
            const response: any = await cursoLivreService.listarPublico();

            const output: CursoParaCard[] = response.data.map((item: any) => new AdminCursoLivre().fromJSON(item) as CursoParaCard);
            const resourceItemsByPage = new Map<string, Promise<any[]>>();
            const getResourceItemsByPage = (pageLink: string): Promise<any[]> => {
                let itemsPromise = resourceItemsByPage.get(pageLink);

                if (!itemsPromise) {
                    itemsPromise = (async () => {
                        const pageResponse: any = await paginaService.buscarPaginaPorLink(pageLink);
                        const pageId = Number(pageResponse?.data?.codigo);

                        if (!pageId) {
                            return [];
                        }

                        const itemsResponse: any = await recursosCostumizadosService.listByPaginaOnlySimple(pageId);
                        return Array.isArray(itemsResponse?.data) ? itemsResponse.data : [];
                    })();
                    resourceItemsByPage.set(pageLink, itemsPromise);
                }

                return itemsPromise;
            };
            const cursosComProgresso: CursoParaCard[] = keycloak.authenticated
                ? await Promise.all(
                      output.map(async curso => {
                          curso.isMatriculado = false;
                          curso.isConcluido = false;
                          curso.progresso = 0;

                          try {
                              const courseUrl = new URL(curso.url, window.location.origin);
                              const pathSegments = courseUrl.pathname.split('/').filter(Boolean);
                              const recursosIndex = pathSegments.indexOf('recursos');

                              if (recursosIndex < 0 || !pathSegments[recursosIndex + 1] || !pathSegments[recursosIndex + 2]) {
                                  return curso;
                              }

                              const pageLink = decodeURIComponent(pathSegments[recursosIndex + 1]);
                              const itemUrl = `/${decodeURIComponent(pathSegments[recursosIndex + 2])}`;
                              const resourceItems = await getResourceItemsByPage(pageLink);
                              const resourceItem = resourceItems.find((item: any) => item.url === itemUrl);

                              if (!resourceItem?.codigo) {
                                  return curso;
                              }

                              const progressResponse: any = await recursosCostumizadosService.listByEtapasAndConteudosResumidos(
                                  resourceItem.codigo
                              );
                              const resourceProgress = progressResponse?.data;
                              const progresso = Math.max(0, Math.min(100, Number(resourceProgress?.percentualConclusao) || 0));

                              curso.isMatriculado = !!resourceProgress?.isIniciado || progresso > 0;
                              curso.isConcluido = !!resourceProgress?.isConcluido || progresso >= 100;
                              curso.progresso = progresso;
                              return curso;
                          } catch {
                              return curso;
                          }
                      })
                  )
                : output;

            /*
             * Cursos exclusivos da Ferramenta de Autoria.
             *
             * Esse endpoint já retorna somente os cursos:
             * - disponíveis para a categoria do usuário;
             * - com turma ativa;
             * - com inscrição aberta;
             * - independentemente de o usuário já ter iniciado.
             */
            if (keycloak.authenticated && keycloak.tokenParsed?.preferred_username) {
                try {
                    const responseExclusive: any = await fadCoursesService.findCoursesExclusive();
                    const exclusiveData: FadCoursesExclusiveModel[] = Array.isArray(responseExclusive?.data) ? responseExclusive.data : [];
                    const mappedExclusiveCursos: CursoParaCard[] = exclusiveData.map(mapExclusiveCourseToCard);

                    setExclusiveCursos(cursosLivresSorter(mappedExclusiveCursos as any) as CursoParaCard[]);
                } catch (error) {
                    console.error('[FAD] Erro ao buscar cursos exclusivos:', error);
                    setExclusiveCursos([]);
                }

                setCursos(cursosLivresSorter(cursosComProgresso as any) as CursoParaCard[]);

                buildViewItemListEvent(output);
            } else {
                setCursos(cursosLivresSorter(cursosComProgresso as any) as CursoParaCard[]);

                setExclusiveCursos([]);
                buildViewItemListEvent(output);
            }

            /*
             * Página intermediária.
             */
            const responsePagina: any = await paginaService.listar();

            const outputPagina: AdminPaginaIntermediaria[] = responsePagina.data.map((item: any) =>
                new AdminPaginaIntermediaria().fromJSON(item)
            );

            if (outputPagina) {
                const paginaOutput: AdminPaginaIntermediaria =
                    outputPagina.find(item => item.pagina === PAGINA_KEY) ?? new AdminPaginaIntermediaria();

                setDadosPagina(paginaOutput);
            }
        } catch (error) {
            console.log('Error listing courses', error);
        } finally {
            setLoading(false);
        }
    };

    useEffect(() => {
        loadData();
        limparRedirecionamentosPosLogin();
    }, [keycloak.authenticated]);

    const buildViewItemListEvent = (cursosParaEnviar: AdminCursoLivre[]) => {
        if (cursosParaEnviar.length !== 0 && !loading) {
            const paramEventList: any[] = [];

            cursosParaEnviar.map((item, index) => {
                paramEventList.push({
                    item_name: item.titulo,
                    item_id: item.codigo.toString(),
                    price: '0',
                    index: index.toString(),
                });
            });

            GTM_GA4_Ecommerce_View_Item_List(paramEventList);
        }
    };

    const matchStatusFilter = (curso: CursoParaCard, filter: StatusFilter): boolean => {
        switch (filter) {
            case 'NAO_INICIADOS':
                return !curso.isMatriculado && !curso.isConcluido;

            case 'EM_ANDAMENTO':
                return !!curso.isMatriculado && !curso.isConcluido;

            case 'CONCLUIDOS':
                return !!curso.isConcluido;

            case 'TODOS':
            default:
                return true;
        }
    };

    const sortCursos = (lista: CursoParaCard[]): CursoParaCard[] => {
        const sorted = [...lista];

        if (!sortOption) {
            return sorted;
        }

        switch (sortOption) {
            case 'MAIS_NOVO':
                return sorted.sort((a, b) => {
                    const dateA = a.dataCriacao ? new Date(a.dataCriacao).getTime() : 0;

                    const dateB = b.dataCriacao ? new Date(b.dataCriacao).getTime() : 0;

                    return dateB - dateA;
                });

            case 'MAIS_ANTIGO':
                return sorted.sort((a, b) => {
                    const dateA = a.dataCriacao ? new Date(a.dataCriacao).getTime() : 0;

                    const dateB = b.dataCriacao ? new Date(b.dataCriacao).getTime() : 0;

                    return dateA - dateB;
                });

            case 'A_Z':
                return sorted.sort((a, b) =>
                    a.titulo.localeCompare(b.titulo, 'pt-BR', {
                        sensitivity: 'base',
                    })
                );

            case 'Z_A':
                return sorted.sort((a, b) =>
                    b.titulo.localeCompare(a.titulo, 'pt-BR', {
                        sensitivity: 'base',
                    })
                );

            default:
                return sorted;
        }
    };

    const {
        sortedInProgressCursos,
        sortedStandardCursosAvailable,
        sortedExclusiveCursos,
        sortedCompletedCursos,
        sortedExclusiveCompletedCursos,
        novosCursosGuest,
        outrosCursosGuest,
    } = useMemo(() => {
        const filteredCursos = cursos.filter(curso => matchStatusFilter(curso, statusFilter));
        const filteredExclusiveCursos = exclusiveCursos.filter(curso => matchStatusFilter(curso, statusFilter));

        const inProgressCursos = [
            ...filteredCursos.filter(c => c.isMatriculado && !c.isConcluido && !isFadCurso(c)),
            ...filteredExclusiveCursos.filter(c => c.isMatriculado && !c.isConcluido),
        ];

        const standardCursosAvailable = filteredCursos.filter(c => !c.isConcluido && !isFadCurso(c));

        const completedCursos = filteredCursos.filter(c => c.isConcluido);

        const standardCompletedCursos = completedCursos.filter(c => !isFadCurso(c));

        const exclusiveCompletedCursos = filteredExclusiveCursos.filter(c => c.isConcluido);

        const standardExclusiveCursos = filteredExclusiveCursos.filter(c => !c.isConcluido);

        const novosCursosGuest = cursos.filter(c => isCursoNovo(c));
        const outrosCursosGuest = cursos.filter(c => !isCursoNovo(c));

        return {
            sortedInProgressCursos: sortCursos(inProgressCursos),
            sortedStandardCursosAvailable: sortCursos(standardCursosAvailable),
            sortedExclusiveCursos: sortCursos(standardExclusiveCursos),
            sortedCompletedCursos: sortCursos(standardCompletedCursos),
            sortedExclusiveCompletedCursos: sortCursos(exclusiveCompletedCursos),
            novosCursosGuest,
            outrosCursosGuest,
        };
    }, [cursos, exclusiveCursos, statusFilter, sortOption]);

    const renderCursosList = (title: string, list: CursoParaCard[], isContinueSection: boolean = false) => {
        if (list.length === 0) {
            return null;
        }

        const isExclusiveSection = title === 'Cursos exclusivos para minha rede';

        return (
            <SectionContainer $exclusive={isExclusiveSection}>
                {isExclusiveSection ? (
                    <ExclusiveSection>
                        <ExclusiveSectionContent>
                            <ExclusiveSectionTitle>{title}</ExclusiveSectionTitle>

                            <CursoLivreCarousel exclusive>
                                {list.map(curso => (
                                    <CardsContainerInner key={curso.codigo}>
                                        <CursoLivreCard
                                            curso={curso}
                                            isContinueSection={isContinueSection}
                                            isNovo={curso.isNovo !== undefined ? curso.isNovo : isCursoNovo(curso)}
                                        />
                                    </CardsContainerInner>
                                ))}
                            </CursoLivreCarousel>
                        </ExclusiveSectionContent>

                        <ExclusiveSectionEllipse src={ElipseWhite} alt="" aria-hidden="true" />
                    </ExclusiveSection>
                ) : (
                    <>
                        <SectionTitle>{title}</SectionTitle>

                        <CursoLivreCarousel>
                            {list.map(curso => (
                                <CardsContainerInner key={curso.codigo}>
                                    <CursoLivreCard
                                        curso={curso}
                                        isContinueSection={isContinueSection}
                                        isNovo={curso.isNovo !== undefined ? curso.isNovo : isCursoNovo(curso)}
                                    />
                                </CardsContainerInner>
                            ))}
                        </CursoLivreCarousel>
                    </>
                )}
            </SectionContainer>
        );
    };

    return (
        <>
            <IntermediatePage
                title1=""
                title2="Cursos"
                subtitle="Cursos gratuitos e de curta duração produzidos pelo Instituto Ayrton Senna."
                helmetTitle="Cursos"
                helmetMetaDescription="Cursos gratuitos e de curta duração produzidos pelo Instituto Ayrton Senna"
                highlightedBanner
                onBackClick={() => {
                    GTM_Page_Intermediaria('cursolivre_intermediaria', 'voltar_pagina_btn');

                    history.push('/');
                }}
            >
                <CursoLivreDetailContainer highlighted>
                    {isLogged && hasValidToken === false && (
                        <TokenActivationBarWrapper>
                            <TokenActivationBar>
                                <TokenActivationTextContainer>
                                    <TokenActivationText>
                                        É parceiro e não está visualizando os cursos da sua rede? Valide o código para ter acesso.
                                    </TokenActivationText>
                                </TokenActivationTextContainer>

                                <TokenActivationButton type="button" onClick={() => setOpenTokenModal(true)}>
                                    Ativar agora
                                </TokenActivationButton>
                            </TokenActivationBar>
                        </TokenActivationBarWrapper>
                    )}

                    <FiltrarCurso
                        isLogged={isLogged}
                        statusFilter={statusFilter}
                        onStatusFilterChange={setStatusFilter}
                        sortOption={sortOption}
                        onSortOptionChange={setSortOption}
                    />

                    {loading ? (
                        <Loading />
                    ) : isLogged ? (
                        <>
                            {renderCursosList('Continue de onde você parou', sortedInProgressCursos, true)}

                            {renderCursosList('Cursos exclusivos para minha rede', [
                                ...sortedExclusiveCursos,
                                ...sortedExclusiveCompletedCursos,
                            ])}

                            {renderCursosList('Cursos Livres', [...sortedStandardCursosAvailable, ...sortedCompletedCursos])}
                        </>
                    ) : (
                        <>
                            {renderCursosList('Novos cursos', novosCursosGuest)}
                            {renderCursosList('Cursos Livres', novosCursosGuest.length > 0 ? outrosCursosGuest : cursos)}
                        </>
                    )}
                </CursoLivreDetailContainer>
            </IntermediatePage>

            <ResgatedToken
                valueModalToken={openTokenModal}
                setHidenModalToken={setOpenTokenModal}
                openModalSolucoesExclusiva={false}
                setOpenModalSolucoesExclusiva={() => {}}
            />

            {!isLogged && showLoginModal && (
                <LoginModalOverlay>
                    <LoginModal>
                        <CloseModalButton type="button" aria-label="Fechar" onClick={() => setShowLoginModal(false)}>
                            ×
                        </CloseModalButton>

                        <LoginModalTitle>
                            Você está na página de
                            <br />
                            Cursos da humane
                        </LoginModalTitle>

                        <LoginModalDescription>
                            Faça login para ter uma
                            <br />
                            navegação ainda mais completa
                        </LoginModalDescription>

                        <LoginModalButton
                            type="button"
                            onClick={() => {
                                login();
                            }}
                        >
                            Fazer login
                        </LoginModalButton>

                        <CreateAccountText>
                            Não tem uma conta?{' '}
                            <CreateAccountLink
                                type="button"
                                onClick={() => {
                                    register();
                                }}
                            >
                                Crie sua conta aqui!
                            </CreateAccountLink>
                        </CreateAccountText>
                    </LoginModal>
                </LoginModalOverlay>
            )}

            {isSearchView && <BarraPesquisa />}
        </>
    );
};

export default CursoLivreDetail;
